import React, { useState, useEffect, useRef } from 'react';
import { MapPin, AlertCircle, Camera, Trophy, Users, ExternalLink, Clock, AlertTriangle, Loader2, Volume2, VolumeX, Settings, LogIn, LogOut, Brain, Video } from 'lucide-react';
import { supabase } from './supabase';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getDeviceId } from './fingerprint';
import { UserReports } from './UserReports';
import { WallOfShame } from './WallOfShame';
import { AdminDashboard } from './components/AdminDashboard';
import { AuthorityDashboard } from './components/AuthorityDashboard';
import { AuthModal } from './components/AuthModal';
import { UpdateNotification } from './components/UpdateNotification';
import { SplashScreen } from './components/SplashScreen';
import { SyncStatus } from './components/SyncStatus';
import { DashcamMode } from './components/DashcamMode';
import { AIPhotoModeSelector } from './components/AIPhotoModeSelector';
import { PhotoUploadMode } from './components/PhotoUploadMode';
import { useAuth } from './hooks/useAuth';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { APP_VERSION } from './version';
import { formatRelativeTime } from './utils/dateFormat';
import { initOfflineStorage, savePendingReport } from './utils/offlineStorage';
import { syncEngine } from './utils/syncEngine';
import { compressImage } from './utils/photoMetadata';
import type { Grade, VergeCondition, AIAnalysisResult } from './types';

// Fix for default marker icons in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface GradeButton {
  grade: Grade;
  description: string;
  color: string;
}

const grades: GradeButton[] = [
  { grade: 'A', description: 'Clean', color: 'bg-green-500' },
  { grade: 'B', description: 'Some small items', color: 'bg-yellow-500' },
  { grade: 'C', description: 'Widespread litter/debris', color: 'bg-orange-500' },
  { grade: 'D', description: 'Serious accumulation', color: 'bg-red-500' }
];

const gradeColors = {
  A: 'green',
  B: 'yellow',
  C: 'orange',
  D: 'red'
};

function isUKMotorway(roadName: string): boolean {
  // Match M followed by numbers, optionally followed by A/B and more numbers
  // Examples: M1, M25, M4, M40, M6 Toll, M6A1
  return /^M\d+(\s*(Toll|[AB]\d+))?$/i.test(roadName);
}

// Function to speak feedback
function speakFeedback(grade: Grade, audioEnabled: boolean) {
  if (audioEnabled && 'speechSynthesis' in window) {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(`Thank you from Clean Up Britain! Grade ${grade} recorded`);
    utterance.lang = 'en-GB';
    utterance.rate = 1;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  }
}

async function determineGradeFromPhoto(blob: Blob, aiModel: 'openai' | 'ollama' = 'openai'): Promise<AIAnalysisResult> {
  const reader = new FileReader();
  const base64Promise = new Promise<string>((resolve, reject) => {
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const imageBase64 = await base64Promise;

  const endpoint = aiModel === 'ollama' ? 'analyze-grade-ollama' : 'analyze-grade';

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${endpoint}`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ imageBase64 })
    }
  );

  if (!response.ok) {
    throw new Error(`AI analysis failed: ${response.status}`);
  }

  const data = await response.json();

  if (data.error || !data.grade) {
    throw new Error(data.error || 'No grade returned from AI');
  }

  return {
    grade: data.grade as Grade,
    confidence: data.confidence,
    reasoning: data.reasoning,
    litter_left_side: Boolean(data.litter_left_side),
    litter_right_side: Boolean(data.litter_right_side),
    litter_central_reservation: Boolean(data.litter_central_reservation),
    dangerous_debris: Boolean(data.dangerous_debris),
    registration_plate_detected: Boolean(data.registration_plate_detected),
  };
}

async function getStreetName(lat: number, lon: number): Promise<string> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Roadside Verge Logger'
        }
      }
    );
    const data = await response.json();
    
    // Try to get the most specific road name available
    const road = data.address.road || 
                 data.address.motorway || 
                 data.address.trunk || 
                 data.address.primary || 
                 data.address.secondary || 
                 data.address.tertiary || 
                 'Unknown road';
    
    return road;
  } catch (error) {
    console.error('Error getting street name:', error);
    return 'Unknown road';
  }
}

async function getLocalAuthority(lat: number, lon: number, roadName: string): Promise<string> {
  // Check if it's a motorway first
  if (isUKMotorway(roadName)) {
    return 'National Highways';
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mapit?lat=${lat}&lon=${lon}`,
      {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch local authority data: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Find the local authority in the response
    // MapIT returns multiple area types, we want the council
    const councils = Object.values(data).filter((area: any) => 
      ['CTY', 'DIS', 'LBO', 'MTD', 'UTA'].includes(area.type)
    );
    
    if (councils.length > 0) {
      return (councils[0] as any).name;
    }
    
    return 'Unknown authority';
  } catch (error) {
    console.error('Error getting local authority:', error);
    return 'Unknown authority';
  }
}

// Function to calculate user ranking
async function calculateUserRanking(deviceId: string | null, conditions: VergeCondition[]): Promise<[number | null, number]> {
  if (!deviceId) return [null, 0];

  // Count reports per device
  const counts = conditions.reduce<Record<string, number>>((acc, { device_id }) => {
    if (device_id) {
      acc[device_id] = (acc[device_id] || 0) + 1;
    }
    return acc;
  }, {});

  // Sort reporters by count
  const sortedReporters = Object.entries(counts)
    .sort(([, a], [, b]) => b - a);

  // Find user's position
  const userPosition = sortedReporters.findIndex(([id]) => id === deviceId) + 1;
  const userCount = counts[deviceId] || 0;

  return [userPosition || null, userCount];
}

function App() {
  const needsUpdate = useVersionCheck();
  const { user, profile, loading: authLoading, isSuperuser, isAuthority, signOut } = useAuth();
  const { isOnline, wasOffline } = useNetworkStatus();

  console.log('App component auth state:', {
    user: user?.email,
    profile: profile?.role,
    isSuperuser,
    isAuthority,
    authLoading
  });

  const forceSignOut = async () => {
    console.log('FORCE SIGN OUT CLICKED - Starting process...');
    alert('Force sign out starting...'); // Immediate feedback
    
    try {
      console.log('Step 1: Clearing Supabase session...');
      // Clear Supabase session with timeout
      const signOutPromise = supabase.auth.signOut();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Sign out timeout')), 3000)
      );
      
      try {
        await Promise.race([signOutPromise, timeoutPromise]);
        console.log('Supabase sign out completed');
      } catch (error) {
        console.log('Supabase sign out failed or timed out:', error);
        // Continue anyway
      }
      
      console.log('Step 2: Clearing storage...');
      // Clear any local storage
      localStorage.clear();
      sessionStorage.clear();
      
      console.log('Step 3: Force reloading page...');
      alert('About to reload page...');
      // Force reload the page
      window.location.reload();
    } catch (error) {
      console.error('Force sign out error:', error);
      alert('Error during force sign out, reloading anyway...');
      // Even if there's an error, reload the page
      window.location.reload();
    }
  };
  
  const [showSplash, setShowSplash] = useState(true);
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [conditions, setConditions] = useState<VergeCondition[]>([]);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [tickerIndex, setTickerIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [userReportCount, setUserReportCount] = useState<number>(0);
  const [userRanking, setUserRanking] = useState<number | null>(null);
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
  const [isAiPhoto, setIsAiPhoto] = useState(false);
  const [showUserReports, setShowUserReports] = useState(false);
  const [showAllReports, setShowAllReports] = useState(false);
  const [showWallOfShame, setShowWallOfShame] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showAuthorityDashboard, setShowAuthorityDashboard] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [aiSuggestedGrade, setAiSuggestedGrade] = useState<Grade | null>(null);
  const [aiAnalysisResult, setAiAnalysisResult] = useState<AIAnalysisResult | null>(null);
  const [autoSubmitCountdown, setAutoSubmitCountdown] = useState<number | null>(null);
  const autoSubmitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingReportsCount, setPendingReportsCount] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string>('');
  const [showDashcamMode, setShowDashcamMode] = useState(false);
  const [showAIPhotoSelector, setShowAIPhotoSelector] = useState(false);
  const [showPhotoUploadMode, setShowPhotoUploadMode] = useState(false);

  useEffect(() => {
    // Hide splash screen after 5 seconds
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setLogoUrl('/clean-up-britain-logo-topaz-sharpen.png');
  }, []);

  // Update rankings whenever conditions or deviceId changes
  useEffect(() => {
    const updateRankings = async () => {
      const [ranking, count] = await calculateUserRanking(deviceId, conditions);
      setUserRanking(ranking);
      setUserReportCount(count);
    };

    if (deviceId) {
      updateRankings();
    }
  }, [conditions, deviceId]);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await initOfflineStorage();
        console.log('Offline storage initialized');

        const visitorId = await getDeviceId();
        setDeviceId(visitorId);

        const pendingCount = await syncEngine.getPendingCount();
        setPendingReportsCount(pendingCount);
      } catch (error) {
        console.error('Error initializing app:', error);
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    const unsubscribePendingCount = syncEngine.onPendingCountChange((count) => {
      setPendingReportsCount(count);
    });

    const unsubscribeStatus = syncEngine.onStatusChange((status) => {
      setSyncStatus(status);
      setIsSyncing(status.includes('Syncing'));
      if (status) {
        setTimeout(() => setSyncStatus(''), 3000);
      }
    });

    return () => {
      unsubscribePendingCount();
      unsubscribeStatus();
    };
  }, []);

  useEffect(() => {
    if (wasOffline && isOnline) {
      console.log('Connection restored, triggering sync...');
      syncEngine.syncAll();
    }
  }, [wasOffline, isOnline]);

  useEffect(() => {
    // Ticker tape animation
    const interval = setInterval(() => {
      if (conditions.length > 0) {
        setIsAnimating(true);
        setTimeout(() => {
          setTickerIndex((current) => (current + 1) % conditions.length);
          setIsAnimating(false);
        }, 500);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [conditions.length]);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation([position.coords.latitude, position.coords.longitude]);
      },
      (error) => {
        console.error('Error getting location:', error);
        setUserLocation([51.505, -0.09]); // Default to London
      }
    );

    fetchConditions();

    const subscription = supabase
      .channel('verge_conditions_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'verge_conditions'
        },
        async (payload) => {
          console.log('Change received!', payload);
          if (payload.eventType === 'INSERT') {
            // Add new report and sort by timestamp
            setConditions(current => {
              const newConditions = [payload.new as VergeCondition, ...current];
              return newConditions.sort((a, b) => 
                new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
              );
            });
            setTickerIndex(0); // Reset ticker to show newest report
          } else if (payload.eventType === 'DELETE') {
            setConditions(current => 
              current.filter(condition => condition.id !== payload.old.id)
            );
          } else if (payload.eventType === 'UPDATE') {
            setConditions(current =>
              current.map(condition =>
                condition.id === payload.new.id
                  ? { ...condition, ...payload.new }
                  : condition
              ).sort((a, b) => 
                new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
              )
            );
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [deviceId]);

  const fetchConditions = async () => {
    try {
      let allConditions: VergeCondition[] = [];
      let count = 0;
      let hasMore = true;

      // Fetch all records in batches
      while (hasMore) {
        const { data, error, count: totalCount } = await supabase
          .from('verge_conditions')
          .select('*', { count: 'exact' })
          .order('timestamp', { ascending: false })
          .range(count, count + 999);

        if (error) throw error;

        if (data) {
          allConditions = [...allConditions, ...data];
          count += data.length;
          hasMore = data.length === 1000; // If we got a full page, there might be more
        } else {
          hasMore = false;
        }

        // Safety check to prevent infinite loops
        if (count > 100000) {
          console.warn('Reached safety limit of 100,000 records');
          break;
        }
      }

      setConditions(allConditions);
    } catch (error) {
      console.error('Error fetching conditions:', error);
      setStatus('Failed to fetch conditions. Please try again later.');
    }
  };

  const uploadPhoto = async (blob: Blob): Promise<string | null> => {
    try {
      const fileName = `${Date.now()}-${deviceId}.jpg`;
      const { data, error } = await supabase.storage
        .from('verge-photos')
        .upload(fileName, blob, {
          contentType: 'image/jpeg'
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('verge-photos')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading photo:', error);
      return null;
    }
  };

  const handlePhotoCapture = async (useAi: boolean) => {
    if (useAi) {
      setShowAIPhotoSelector(true);
    } else {
      clearAutoSubmitTimer();
      setIsAiPhoto(false);
      if (!fileInputRef.current) return;
      fileInputRef.current.click();
    }
  };

  const handleTakePhotoNow = () => {
    clearAutoSubmitTimer();
    setIsAiPhoto(true);
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  const handleUploadPhotosClick = () => {
    setShowPhotoUploadMode(true);
  };

  const handleDashcamPhoto = async (blob: Blob): Promise<{ success: boolean; grade?: Grade }> => {
    try {
      const aiModel = profile?.ai_model || 'openai';
      const aiResult = await determineGradeFromPhoto(blob, aiModel);
      await logCondition(aiResult.grade, blob, aiResult);
      speakFeedback(aiResult.grade, audioEnabled);
      return { success: true, grade: aiResult.grade };
    } catch (error) {
      console.error('Error processing dashcam photo:', error);
      return { success: false };
    }
  };

  const handleUploadedPhoto = async (
    blob: Blob,
    latitude: number,
    longitude: number,
    timestamp: string
  ): Promise<{ success: boolean; grade?: Grade }> => {
    try {
      const aiModel = profile?.ai_model || 'openai';
      const aiResult = await determineGradeFromPhoto(blob, aiModel);

      let street_name = 'Unknown road';
      let local_authority = 'Unknown authority';

      if (isOnline) {
        try {
          street_name = await getStreetName(latitude, longitude);
          local_authority = await getLocalAuthority(latitude, longitude, street_name);
        } catch (error) {
          console.error('Error fetching location details:', error);
        }
      }

      let photo_url = null;
      if (blob) {
        photo_url = await uploadPhoto(blob);
      }

      const { data, error } = await supabase
        .from('verge_conditions')
        .insert([{
          grade: aiResult.grade,
          latitude,
          longitude,
          timestamp,
          street_name,
          local_authority,
          device_id: deviceId,
          photo_url,
          litter_left_side: aiResult.litter_left_side,
          litter_right_side: aiResult.litter_right_side,
          litter_central_reservation: aiResult.litter_central_reservation,
          dangerous_debris: aiResult.dangerous_debris,
          registration_plate_detected: aiResult.registration_plate_detected,
        }])
        .select();

      if (error) throw error;

      if (data && data[0]) {
        setConditions(current => {
          const newConditions = [data[0] as VergeCondition, ...current];
          return newConditions.sort((a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
        });
      }

      return { success: true, grade: aiResult.grade };
    } catch (error) {
      console.error('Error processing uploaded photo:', error);
      return { success: false };
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      console.log('[Photo Upload] Compressing image with EXIF preservation...');
      const blob = await compressImage(file);
      setPhotoBlob(blob);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (isAiPhoto) {
        setStatus('AI is analyzing your photo...');
        try {
          const aiModel = profile?.ai_model || 'openai';
          const aiResult = await determineGradeFromPhoto(blob, aiModel);
          setAiSuggestedGrade(aiResult.grade);
          setAiAnalysisResult(aiResult);
          startAutoSubmitTimer(aiResult.grade, blob, aiResult);
        } catch (error) {
          console.error('Error analyzing photo:', error);
          setStatus('Photo captured! AI analysis failed, please select grade manually.');
          setTimeout(() => setStatus(''), 3000);
        }
      } else {
        setStatus('Photo captured successfully! Ready to submit with your report.');
        setTimeout(() => setStatus(''), 3000);
      }
    } catch (error) {
      console.error('Error processing photo:', error);
      setStatus('Failed to process photo. Please try again.');
      setTimeout(() => setStatus(''), 3000);
    }
  };

  const startAutoSubmitTimer = (grade: Grade, blob: Blob, aiResult?: AIAnalysisResult) => {
    setAutoSubmitCountdown(5);
    setStatus(`AI suggests Grade ${grade}. Auto-submitting in 5 seconds... Click any grade to override.`);

    // Start countdown interval
    countdownIntervalRef.current = setInterval(() => {
      setAutoSubmitCountdown(prev => {
        if (prev === null || prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    // Set auto-submit timeout - pass blob directly to avoid stale closure
    autoSubmitTimeoutRef.current = setTimeout(() => {
      logCondition(grade, blob, aiResult);
    }, 5000);
  };

  const clearAutoSubmitTimer = () => {
    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setAiSuggestedGrade(null);
    setAutoSubmitCountdown(null);
    setStatus('');
  };

  const resetPhotoState = () => {
    setPhotoBlob(null);
    setIsAiPhoto(false);
    setAiAnalysisResult(null);
    clearAutoSubmitTimer();
  };

  const logCondition = async (grade: Grade, explicitPhotoBlob?: Blob | null, aiResult?: AIAnalysisResult) => {
    clearAutoSubmitTimer();
    setLoading(true);

    // Use explicit blob if provided (from AI auto-submit), otherwise use state
    const blobToUpload = explicitPhotoBlob !== undefined ? explicitPhotoBlob : photoBlob;

    console.log('logCondition called:', {
      grade,
      hasExplicitBlob: explicitPhotoBlob !== undefined,
      hasStateBlob: photoBlob !== null,
      blobToUpload: blobToUpload !== null
    });

    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser');
      }

      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const { latitude, longitude } = position.coords;
      const timestamp = new Date().toISOString();

      let street_name = 'Unknown road';
      let local_authority = 'Unknown authority';

      if (isOnline) {
        try {
          street_name = await getStreetName(latitude, longitude);
          local_authority = await getLocalAuthority(latitude, longitude, street_name);
        } catch (error) {
          console.error('Error fetching location details:', error);
        }
      }

      if (!isOnline) {
        setStatus('Saving report offline...');
        await savePendingReport({
          grade,
          latitude,
          longitude,
          timestamp,
          street_name,
          local_authority,
          device_id: deviceId,
          photo_blob: blobToUpload,
          litter_left_side: aiResult?.litter_left_side,
          litter_right_side: aiResult?.litter_right_side,
          litter_central_reservation: aiResult?.litter_central_reservation,
          dangerous_debris: aiResult?.dangerous_debris,
          registration_plate_detected: aiResult?.registration_plate_detected,
        });

        const newCount = await syncEngine.getPendingCount();
        setPendingReportsCount(newCount);

        speakFeedback(grade, audioEnabled);
        setStatus(`Grade ${grade} saved offline! Will sync when connection is restored.`);
        resetPhotoState();
        setTimeout(() => setStatus(''), 3000);
        return;
      }

      setStatus('Uploading report, please wait...');

      let photo_url = null;
      if (blobToUpload) {
        setStatus('Uploading photo...');
        console.log('Uploading photo, blob size:', blobToUpload.size);
        photo_url = await uploadPhoto(blobToUpload);
        if (photo_url) {
          console.log('Photo uploaded successfully:', photo_url);
        } else {
          console.error('Photo upload failed, returned null');
          setStatus('Warning: Photo upload failed, but saving report...');
        }
      }

      setStatus('Saving report...');
      const { data, error } = await supabase
        .from('verge_conditions')
        .insert([
          {
            grade,
            latitude,
            longitude,
            timestamp,
            street_name,
            local_authority,
            device_id: deviceId,
            photo_url,
            litter_left_side: aiResult?.litter_left_side ?? false,
            litter_right_side: aiResult?.litter_right_side ?? false,
            litter_central_reservation: aiResult?.litter_central_reservation ?? false,
            dangerous_debris: aiResult?.dangerous_debris ?? false,
            registration_plate_detected: aiResult?.registration_plate_detected ?? false,
          }
        ])
        .select();

      if (error) throw error;

      if (data && data[0]) {
        setConditions(current => {
          const newConditions = [data[0] as VergeCondition, ...current];
          return newConditions.sort((a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
        });
        setTickerIndex(0);
        console.log('Report saved with photo_url:', data[0].photo_url);
      }

      speakFeedback(grade, audioEnabled);

      setStatus(`Successfully logged Grade ${grade} at ${street_name} (${local_authority})`);
      resetPhotoState();
      setTimeout(() => setStatus(''), 3000);
    } catch (error) {
      console.error('Error in logCondition:', error);
      setStatus(error instanceof Error ? error.message : 'An error occurred');
      resetPhotoState();
    } finally {
      setLoading(false);
    }
  };

  const handleManualSync = async () => {
    if (!isOnline) {
      setStatus('Cannot sync while offline. Please check your connection.');
      setTimeout(() => setStatus(''), 3000);
      return;
    }

    setIsSyncing(true);
    await syncEngine.syncAll();
    setIsSyncing(false);
  };

  const poorConditionCount = conditions.filter(c => c.grade === 'C' || c.grade === 'D').length;

  if (showSplash) {
    return <SplashScreen />;
  }

  if (showUserReports) {
    return (
      <UserReports
        reports={conditions.filter(report => report.device_id === deviceId)}
        onBack={() => setShowUserReports(false)}
        deviceId={deviceId}
      />
    );
  }

  if (showAllReports) {
    return (
      <UserReports
        reports={conditions}
        onBack={() => setShowAllReports(false)}
        deviceId={null}
      />
    );
  }

  if (showWallOfShame) {
    return (
      <WallOfShame
        reports={conditions}
        onBack={() => setShowWallOfShame(false)}
      />
    );
  }

  if (showAdminDashboard) {
    return (
      <AdminDashboard
        onBack={() => setShowAdminDashboard(false)}
      />
    );
  }

  // Authority users should always see their dashboard (unless they're also a superuser)
  if (isAuthority && !isSuperuser && profile?.local_authority && profile?.id) {
    return (
      <AuthorityDashboard
        onBack={signOut}
        localAuthority={profile.local_authority}
        userProfileId={profile.id}
      />
    );
  }

  if (showAuthorityDashboard && profile?.local_authority && profile?.id) {
    return (
      <AuthorityDashboard
        onBack={() => setShowAuthorityDashboard(false)}
        localAuthority={profile.local_authority}
        userProfileId={profile.id}
      />
    );
  }

  const currentCondition = conditions[tickerIndex];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden w-full">
      {/* Online/Offline Status - Fixed Top Left */}
      <div className="fixed top-2 left-2 z-50 max-w-[200px]">
        <SyncStatus
          isOnline={isOnline}
          pendingCount={pendingReportsCount}
          isSyncing={isSyncing}
          onManualSync={handleManualSync}
        />
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        <header className="text-center mb-4">
          {/* Auth and Superuser Controls */}
          <div className="flex justify-between items-center mb-4">
            <div className="w-10" /> {/* Spacer */}
            <div className="flex-1" /> {/* Spacer */}
            <div className="flex items-center gap-2">
              {user ? (
                <>
                  {isSuperuser && (
                    <button
                      onClick={() => setShowAdminDashboard(true)}
                      className="flex items-center gap-1 px-3 py-1 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                    >
                      <Settings className="w-4 h-4" />
                      Admin
                    </button>
                  )}
                  {isAuthority && (
                    <button
                      onClick={() => setShowAuthorityDashboard(true)}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                    >
                      <Settings className="w-4 h-4" />
                      My Reports
                    </button>
                  )}
                  <span className="text-sm text-gray-600">{user.email}</span>
                  <button
                    onClick={signOut}
                    className="flex items-center gap-1 px-3 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                  <button
                    onClick={forceSignOut}
                    className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-bold"
                    title="Force sign out and reload"
                  >
                    <LogOut className="w-3 h-3" />
                    FORCE RESET
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </button>
              )}
            </div>
          </div>

          <div className="flex justify-center mb-4">
            <a 
              href="https://cleanupbritain.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-90 transition-opacity"
            >
              {logoUrl ? (
                <img 
                  src={logoUrl}
                  alt="Clean Up Britain Logo" 
                  className="h-16 w-auto"
                />
              ) : (
                <div className="h-16 w-32 bg-gray-200 animate-pulse rounded" />
              )}
            </a>
          </div>
          <div className="mb-2">
            <h1 className="text-3xl font-bold text-gray-800">RoadVlog</h1>
            <p className="text-lg text-gray-600">Roadside Verge Condition Logger</p>
          </div>
          <div className="flex items-center justify-center text-gray-600">
            <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
            <span className="text-sm">Help us collect data! Location, road/street and local authority will be auto-logged. Passenger use only!</span>
          </div>
          <div className="flex flex-col items-center gap-2 mt-2">
            {userReportCount > 0 && (
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  {userRanking ? (
                    <>You're our #{userRanking} top reporter with {userReportCount} report{userReportCount === 1 ? '' : 's'}</>
                  ) : (
                    <>You've submitted {userReportCount} report{userReportCount === 1 ? '' : 's'}</>
                  )}
                </span>
                <button
                  onClick={() => setShowUserReports(true)}
                  className="text-indigo-600 hover:text-indigo-800 transition-colors inline-flex items-center"
                >
                  <ExternalLink className="w-4 h-4 ml-1" />
                  <span className="text-sm font-medium ml-1">View</span>
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span className="text-sm text-gray-700">Total reports: {conditions.length}</span>
              <button
                onClick={() => setShowAllReports(true)}
                className="text-emerald-600 hover:text-emerald-800 transition-colors inline-flex items-center"
              >
                <ExternalLink className="w-4 h-4 ml-1" />
                <span className="text-sm font-medium ml-1">View</span>
              </button>
            </div>
            {poorConditionCount > 0 && (
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="text-sm text-gray-700">{poorConditionCount} poor condition report{poorConditionCount === 1 ? '' : 's'}</span>
                <button
                  onClick={() => setShowWallOfShame(true)}
                  className="text-red-500 hover:text-red-700 transition-colors inline-flex items-center"
                >
                  <ExternalLink className="w-4 h-4 ml-1" />
                  <span className="text-sm font-medium ml-1">Wall of Shame</span>
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Latest Reports */}
        {conditions.length > 0 && currentCondition && (
          <div className="bg-gray-50 rounded-lg shadow-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-800">Latest Reports</h2>
              <div className="flex items-center text-gray-500 text-sm">
                <Clock className="w-4 h-4 mr-1 flex-shrink-0" />
                {formatRelativeTime(currentCondition.timestamp)}
              </div>
            </div>
            <div className={`flex items-center gap-4 ${isAnimating ? 'animate-slide-out' : 'animate-slide-in'}`}>
              <div className={`${grades.find(g => g.grade === currentCondition.grade)?.color} text-white px-3 py-1 rounded-full text-sm font-medium flex-shrink-0`}>
                Grade {currentCondition.grade}
              </div>
              <div className="flex-grow min-w-0">
                <p className="text-gray-800 font-medium truncate">{currentCondition.street_name || 'Unknown road'}</p>
                {currentCondition.local_authority && (
                  <p className="text-gray-500 text-sm truncate">{currentCondition.local_authority}</p>
                )}
              </div>
              {currentCondition.photo_url && (
                <img 
                  src={currentCondition.photo_url} 
                  alt="Verge condition" 
                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                />
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between text-gray-700">
          <div className="font-medium">
            Got something to report? Take a photo (optional) and choose Grade A-D
          </div>
          <button
            onClick={() => setAudioEnabled(!audioEnabled)}
            className={`p-2 rounded-full transition-colors ${
              audioEnabled ? 'text-blue-600 hover:text-blue-700' : 'text-gray-400 hover:text-gray-500'
            }`}
            title={`Audio feedback ${audioEnabled ? 'on' : 'off'}`}
          >
            {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </div>

        {/* Photo Capture Buttons */}
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => handlePhotoCapture(false)}
            disabled={loading}
            className={`p-3 rounded-lg shadow-lg transition-all duration-200
              flex items-center justify-center ${
                photoBlob && !isAiPhoto
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-blue-500 hover:bg-blue-600'
              } text-white disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Camera className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm font-medium">{photoBlob && !isAiPhoto ? 'Photo Ready ✓' : 'Take Photo'}</span>
          </button>
          <button
            onClick={() => handlePhotoCapture(true)}
            disabled={loading}
            className={`p-3 rounded-lg shadow-lg transition-all duration-200
              flex items-center justify-center ${
                photoBlob && isAiPhoto
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-indigo-500 hover:bg-indigo-600'
              } text-white disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Brain className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm font-medium">{photoBlob && isAiPhoto ? 'AI Ready ✓' : 'AI Photo'}</span>
          </button>
          <button
            onClick={() => setShowDashcamMode(true)}
            disabled={!isSuperuser || loading}
            className={`p-3 rounded-lg shadow-lg transition-all duration-200
              flex items-center justify-center text-white
              ${isSuperuser
                ? 'bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed'
                : 'bg-gray-400 opacity-50 cursor-not-allowed'}`}
          >
            <Video className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm font-medium">Dashcam Mode</span>
          </button>
        </div>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Grade Buttons */}
        <div className="grid gap-2">
          {grades.map(({ grade, description, color }) => (
            <button
              key={grade}
              onClick={() => {
                clearAutoSubmitTimer();
                logCondition(grade, undefined, aiAnalysisResult ?? undefined);
              }}
              disabled={loading}
              className={`${color} text-white p-2 rounded-lg shadow-lg hover:opacity-90 
                transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center justify-center min-h-[48px] relative border-2 border-transparent
                ${aiSuggestedGrade === grade ? 'animate-pulse-border' : ''}
                ${autoSubmitCountdown !== null && aiSuggestedGrade !== grade ? 'opacity-60' : ''}`}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" />
              ) : (
                <div className="flex items-center justify-center gap-2">
                  {aiSuggestedGrade === grade && (
                    <Brain className="w-5 h-5 flex-shrink-0" />
                  )}
                  <span className="font-bold text-base">
                    Grade {grade} - {description}
                    {autoSubmitCountdown !== null && aiSuggestedGrade === grade && (
                      <span className="ml-2 text-lg">({autoSubmitCountdown})</span>
                    )}
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Copyright */}
        <div className="text-center text-sm text-gray-500 mt-8 pb-4">
          © <a href="https://start-software.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 underline">Start Software</a> 2025
          <span className="mx-2">•</span>
          <span className="text-gray-400">v{APP_VERSION}</span>
        </div>

        {needsUpdate && <UpdateNotification />}

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />

        <AIPhotoModeSelector
          isOpen={showAIPhotoSelector}
          onClose={() => setShowAIPhotoSelector(false)}
          onTakePhoto={handleTakePhotoNow}
          onUploadPhotos={handleUploadPhotosClick}
        />

        <PhotoUploadMode
          isOpen={showPhotoUploadMode}
          onClose={() => setShowPhotoUploadMode(false)}
          onProcessPhoto={handleUploadedPhoto}
          audioEnabled={audioEnabled}
        />

        <DashcamMode
          isOpen={showDashcamMode}
          onClose={() => setShowDashcamMode(false)}
          onPhotoCapture={handleDashcamPhoto}
          audioEnabled={audioEnabled}
        />

        {(status || syncStatus) && (
          <div className={`fixed bottom-4 left-4 right-4 p-4 rounded-lg shadow-lg
            ${(status.includes('Successfully') || syncStatus.includes('Successfully')) ? 'bg-green-500' :
              (status.includes('Uploading') || status.includes('Saving') || status.includes('offline') || syncStatus.includes('Syncing')) ? 'bg-blue-500' : 'bg-red-500'}
            text-white flex items-center justify-center gap-2 max-w-full mx-auto`}>
            {(status.includes('Uploading') || status.includes('Saving') || syncStatus.includes('Syncing')) ? (
              <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="text-sm">{syncStatus || status}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;